// =============================================================================
// supplier-team-client.ts — Client API de la page « Mon équipe / Collaborateurs » FOURNISSEUR
// (DBU §3.7, F4). Zone réservée au contact MASTER du SIRET.
// =============================================================================
// Miroir HTTP des endpoints FOURNISSEUR du BC supplier-reference
// (apps/api/src/supplier-reference/supplier-reference-supplier.controller.ts), session opaque sous
// withTenant(supplier_siret) :
//   • GET  /supplier/contacts        → annuaire des contacts du SIRET (MASTER + collaborateurs) ;
//   • POST /supplier/contacts/invite → inviter un collaborateur (nom/prénom/email) ;
//   • POST /supplier/contacts/revoke → révoquer un collaborateur (garde [F1], R-3).
//
// AUTORITÉ = API : le siret ET l'identité MASTER (invitant) viennent de la SESSION, JAMAIS du body
// (INVARIANT n°1). L'ACL MASTER-only (§3.7) + le mur légal domaine + la garde [F1] sont serveur.
// L'UI ne réinterprète AUCUNE règle métier. AUCUNE donnée métier en localStorage (RULE_REF_006).
// Horodatages = ISO 8601 (string), jamais d'objet Date.

/** Rôle d'un membre de l'équipe (renfort TEXTE, jamais une couleur seule — WCAG 2.1 AA). */
export type SupplierTeamRoleDto = 'MASTER' | 'COLLABORATEUR';

/**
 * Statut d'activation DÉRIVÉ du lien (miroir HTTP de `TokenActivationStatus` côté API identity,
 * ActivationStatusService). Renfort TEXTE en UI (« Envoyé / Expiré / Activé »), jamais une couleur
 * seule. `null` = aucune identité émise pour le contact (hors périmètre) — l'UI affiche « — ».
 */
export type TokenActivationStatusDto = 'Envoye' | 'Expire' | 'Active';

/** Statut du lien d'activation d'un contact (miroir de `TokenStatusView`, dérivé côté identity). */
export interface ContactTokenStatusDto {
  readonly contactId: string;
  readonly status: TokenActivationStatusDto | null;
}

/** État d'un contact (parité enum ContactState côté API). Renfort TEXTE en UI. */
export type SupplierTeamStateDto = 'Invite' | 'Actif' | 'Inactif' | 'ResignatureRequise';

/** Un membre de l'annuaire §3.7 : identité nominative + rôle + état + dates (OWNED supplier-reference). */
export interface SupplierTeamMemberDto {
  readonly contactId: string;
  readonly nom: string;
  readonly prenom: string;
  readonly email: string;
  readonly role: SupplierTeamRoleDto;
  readonly state: SupplierTeamStateDto;
  /** Date d'invitation (ISO 8601) ; null pour le MASTER (désigné, non « invité »). */
  readonly invitedAt: string | null;
  /** Dernière activité connue (ISO 8601) ; null tant qu'aucun accès observé. */
  readonly lastActivityAt: string | null;
  /**
   * S37-ANNUAIRE-UX #19 — Date de dernière SAISIE TJM (ISO 8601), projetée CROSS-BC depuis
   * rate-collection (miroir de `SupplierTeamMemberView.lastSubmissionAt`). Horodatage non-nominatif.
   * `null` si le contact n'a jamais saisi — ou si le carrier rate-collection n'est pas encore branché.
   */
  readonly lastSubmissionAt: string | null;
}

/** Réponse de l'annuaire §3.7 : membres du SIRET (MASTER + collaborateurs) + horodatage de génération. */
export interface SupplierTeamDto {
  readonly generatedAt: string;
  readonly members: readonly SupplierTeamMemberDto[];
  /**
   * R15-UX-02 — plafond de collaborateurs paramétrable (DBU §3.7 l.3108). Miroir HTTP de
   * `SupplierTeamView`. `collaboratorCap` = valeur du paramètre GLOBAL (0 = AUCUN plafond, défaut V1) ;
   * `liveCollaboratorCount` = collaborateurs vivants comptés pour le plafond ; `capReached` = plafond
   * atteint. L'UI affiche le plafond et désactive/explique l'action d'invitation quand `capReached` —
   * l'autorité de l'enforcement (409) reste l'API ; l'UI ne réinterprète aucune règle.
   */
  readonly collaboratorCap: number;
  readonly liveCollaboratorCount: number;
  readonly capReached: boolean;
}

/** Corps d'invitation d'un collaborateur (nom/prénom/email). L'invitant vient de la SESSION (jamais ici). */
export interface InviteCollaboratorInput {
  readonly nom: string;
  readonly prenom: string;
  readonly collaboratorEmail: string;
}

/** Résultat d'une invitation réussie : l'id du collaborateur créé (au statut Invité). */
export interface InviteCollaboratorResultDto {
  readonly newContactId: string;
}

/** Corps de révocation d'un collaborateur PAR le MASTER (garde [F1] : jamais un master). */
export interface RevokeCollaboratorInput {
  readonly contactId: string;
  readonly reason: string;
}

/** Genre d'erreur applicative de la page équipe, mappé pour un message UX explicite. */
export type SupplierTeamApiErrorKind =
  | 'unauthorized'
  | 'forbidden'
  | 'not-found'
  | 'domain-mismatch'
  | 'conflict'
  // 429 anti-abus (ActivationResendRateLimitError, plafond 3/24h) — renvoi de lien d'activation.
  | 'rate-limited'
  | 'invalid-value'
  | 'generic';

/**
 * Erreur applicative renvoyée par l'API équipe (HTTP 4xx/5xx). L'UI l'affiche telle quelle ; elle ne
 * réinterprète JAMAIS la règle métier (autorité = API). Le `kind` permet un libellé UX ciblé.
 */
export class SupplierTeamApiError extends Error {
  public readonly kind: SupplierTeamApiErrorKind;

  public constructor(message: string, kind: SupplierTeamApiErrorKind = 'generic') {
    super(message);
    this.name = 'SupplierTeamApiError';
    this.kind = kind;
  }
}

/**
 * Contrat du client équipe FOURNISSEUR — miroir des endpoints §3.7. Le siret + l'identité MASTER
 * viennent de la session (jamais du body) ; l'ACL/le mur légal/la garde [F1] sont l'autorité de l'API.
 */
export interface SupplierTeamClient {
  /** GET annuaire des contacts du SIRET du MASTER en session (MASTER + collaborateurs). */
  listTeam(): Promise<SupplierTeamDto>;
  /**
   * GET statut du lien d'activation (Envoyé/Expiré/Activé) des contacts du SIRET en session.
   * GET /supplier/contacts/token-status — dérivé côté identity (jamais de hash/secret/email exposé).
   */
  listTokenStatus(): Promise<readonly ContactTokenStatusDto[]>;
  /** POST invitation d'un collaborateur (nom/prénom/email ; invitant = session). */
  inviteCollaborator(input: InviteCollaboratorInput): Promise<InviteCollaboratorResultDto>;
  /**
   * POST renvoi du lien d'activation à un contact NON activé (anti-abus 3/24h côté API).
   * POST /supplier/contacts/:contactId/resend-activation. Erreurs métier par CODE :
   * 429 (rate-limited), 409 (conflict = déjà activé), 404 (not-found = contact absent/cross-tenant).
   */
  resendActivation(contactId: string): Promise<void>;
  /** POST révocation d'un collaborateur PAR le MASTER (garde [F1]). */
  revokeCollaborator(input: RevokeCollaboratorInput): Promise<void>;
}
